import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { sanitizeHtml } from "../lib/sanitize";
import { Button } from "../components/ui/button";
import {
  Bot,
  Send,
  Paperclip,
  FileText,
  UploadCloud,
  Sparkles,
  Workflow,
  MessageSquare,
  RefreshCw,
  CheckCircle2,
  X,
  User,
  ChevronRight,
  ChevronDown,
  Camera,
  AlertTriangle,
  ScanSearch,
  Book,
  HelpCircle,
  History,
} from "lucide-react";
import { sampleFlow, suggestedPrompts } from "../data/flowMock";
import { listFlows, getActiveId, setActiveId, getFlow, loadFlows } from "../lib/flowStorage";
import { guruApi, manualsApi, diagnosticSessionsApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { GuruActionCard, GuruResultCard, GuruLinkCard } from "../components/guru/GuruActionCard";
import KnowledgeBaseModal from "../components/KnowledgeBaseModal";
import DiagnosticHistoryModal from "../components/guru/DiagnosticHistoryModal";
import { toast } from "sonner";

let _gid = 0;
const uid = () => `gm-${Date.now()}-${_gid++}`;

// A KB citation always shows the source document name + the relevant
// section/chunk it came from — never just a bare filename.
const sourceLabel = (s) => {
  if (typeof s === "string") return s;
  const chunk = s.chunkIndex;
  return `${s.filename || "source"}${chunk != null ? ` \u00b7 Section ${chunk + 1}` : ""}`;
};

const STORAGE_KEY = "servicepro_flow_v1"; // legacy
const DIAG_SESSION_KEY = "servicepro_diag_session_id"; // pointer only — all state lives on the backend

function loadFlow() {
  const id = getActiveId();
  if (id) {
    const f = getFlow(id);
    if (f) return f;
  }
  // Fallback: read legacy storage or seed sample
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (_) {}
  }
  return sampleFlow;
}

// Turns a backend DiagnosticSession history entry into a chat bubble.
function historyToMessage(entry) {
  if (entry.role === "user") {
    return { id: entry.id, sender: "user", text: entry.text };
  }
  const base = { id: entry.id, sender: "bot", text: entry.text, node: { id: entry.nodeId, type: entry.nodeType } };
  if (entry.nodeType === "diagnosisNode") {
    return { ...base, diagnosis: true, detail: entry.detail };
  }
  if (entry.nodeType === "recommendProductNode") {
    return { ...base, recommendation: true, products: entry.products || [] };
  }
  return base;
}

// Turns a backend DiagnosticSession chatHistory entry (Free Chat) into a chat bubble.
function chatEntryToMessage(entry) {
  if (entry.role === "user") {
    return { id: entry.id, sender: "user", text: entry.text };
  }
  const base = { id: entry.id, sender: "bot", text: entry.text };
  if (entry.flowSuggestion) {
    return { ...base, flowSuggestion: entry.flowSuggestion };
  }
  if (entry.sources && entry.sources.length > 0) {
    return { ...base, sources: entry.sources.map((s) => sourceLabel(s)) };
  }
  return base;
}

export default function Guru() {
  const navigate = useNavigate();
  const { hasModule } = useAuth();
  const diagnosticsEnabled = hasModule("ai_diagnostics");
  const [mode, setMode] = useState("guided"); // 'guided' | 'free' — purely a display toggle; both share one DiagnosticSession
  const [files, setFiles] = useState([]);
  const [guidedMessages, setGuidedMessages] = useState([]);
  const [freeMessages, setFreeMessages] = useState([]);
  const messages = mode === "guided" ? guidedMessages : freeMessages;
  const setMessages = mode === "guided" ? setGuidedMessages : setFreeMessages;
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [flow, setFlow] = useState(loadFlow());
  const [flowsList, setFlowsList] = useState(() => listFlows());
  const [diagId, setDiagId] = useState(null); // ONE DiagnosticSession shared by Guided Mode + Free Chat
  const [currentNode, setCurrentNode] = useState(null);
  const [askInput, setAskInput] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleReindex = async () => {
    setReindexing(true);
    try {
      const res = await manualsApi.reindex();
      toast.success(`Re-indexed ${res.reindexed} of ${res.total} source(s)`);
      manualsApi.list().then((list) =>
        setFiles(
          list.map((m) => ({
            id: m.id,
            name: m.name,
            type: m.ext,
            status: m.status,
            chunks: m.chunks,
            date: (m.uploadedAt || "").slice(0, 10),
          })),
        ),
      );
    } catch (e) {
      toast.error("Re-index failed");
    } finally {
      setReindexing(false);
    }
  };
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // On narrow viewports, use the dedicated mobile Guru chat experience.
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      navigate("/mobile/guru", { replace: true });
    }
  }, [navigate]);

  // Load manuals from API on mount
  useEffect(() => {
    if (!diagnosticsEnabled) return;
    manualsApi
      .list()
      .then((list) =>
        setFiles(
          list.map((m) => ({
            id: m.id,
            name: m.name,
            size: `${(m.size / 1024).toFixed(0)} KB`,
            date: (m.uploadedAt || "").slice(0, 10),
            type: (m.ext || "").replace(".", "") || "pdf",
            status: m.status || "Indexed",
            chunks: m.chunks,
          })),
        ),
      )
      .catch((err) => console.error("Failed to load manuals", err));
  }, []);

  // Listen for flow saves from FlowChart page
  useEffect(() => {
    const handler = () => {
      setFlow(loadFlow());
      setFlowsList(listFlows());
    };
    window.addEventListener("flow-saved", handler);
    window.addEventListener("flows-changed", handler);
    window.addEventListener("active-flow-changed", handler);
    window.addEventListener("storage", handler);
    loadFlows().then(handler);
    return () => {
      window.removeEventListener("flow-saved", handler);
      window.removeEventListener("flows-changed", handler);
      window.removeEventListener("active-flow-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const switchFlow = async (id) => {
    setActiveId(id);
    const f = getFlow(id);
    if (!f || !diagId) return;
    setFlow(f);
    if (mode === "guided") {
      await startFlowchartOnSession(diagId, f.id, `Switched to **"${f.name}"**. Let's begin.`);
    }
  };

  // (Re)attaches a flowchart to the SHARED session and seeds `guidedMessages`
  // from the backend's returned history. Always resets the guided transcript
  // to a clean slate for this flowchart — Free Chat's history is untouched.
  const startFlowchartOnSession = useCallback(async (sid, flowchartId, introText) => {
    try {
      const started = await diagnosticSessionsApi.startFlowchart(sid, flowchartId);
      setCurrentNode(started.currentNode || null);
      setGuidedMessages([
        { id: uid(), sender: "bot", text: introText },
        ...(started.history || []).map(historyToMessage),
      ]);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't start the diagnostic session.");
      setGuidedMessages([{ id: uid(), sender: "bot", text: "Couldn't start a diagnostic session. Please try again." }]);
    }
  }, []);

  // Resumes or creates the ONE DiagnosticSession this page uses for both
  // Guided Mode and Free Chat, and seeds both transcripts from it. Runs once
  // on mount — switching the Guided/Free Chat tabs afterwards never re-fetches
  // or resets anything, so neither conversation is ever lost.
  const initSession = useCallback(async () => {
    let session = null;
    const storedId = localStorage.getItem(DIAG_SESSION_KEY);
    if (storedId) {
      try {
        session = await diagnosticSessionsApi.get(storedId);
      } catch (_) {
        session = null;
      }
    }
    if (!session) {
      session = await diagnosticSessionsApi.create({});
      localStorage.setItem(DIAG_SESSION_KEY, session.id);
    }
    setDiagId(session.id);

    setFreeMessages(
      (session.chatHistory || []).length > 0
        ? session.chatHistory.map(chatEntryToMessage)
        : [{
            id: uid(),
            sender: "bot",
            text: `Hi, I'm **Diagnostic Guru**. Describe an issue and I'll check for a matching company procedure first, or ask a diagnostic question. I can also **build an estimate, convert it to an invoice, email a document, book an appointment, create a task, or message a teammate** (you confirm before anything is saved).`,
          }],
    );

    if (session.flowchartId) {
      await loadFlows();
      const resumedFlow = getFlow(session.flowchartId);
      if (resumedFlow) {
        setFlow(resumedFlow);
        setActiveId(resumedFlow.id);
      }
      setCurrentNode(session.currentNode || null);
      setGuidedMessages([
        { id: uid(), sender: "bot", text: `Welcome back \u2014 resuming **"${session.flowchartName || resumedFlow?.name || "your flow"}"** right where you left off.` },
        ...(session.history || []).map(historyToMessage),
      ]);
      return;
    }

    await loadFlows();
    const activeFlowId = getActiveId();
    const f = activeFlowId ? getFlow(activeFlowId) : null;
    if (!f) {
      setGuidedMessages([{ id: 1, sender: "bot", text: "No diagnostic flow available. Build one in the Flow Chart page first." }]);
      return;
    }
    setFlow(f);
    await startFlowchartOnSession(
      session.id,
      f.id,
      `Hi, I'm **Diagnostic Guru** \u2014 your diagnostic assistant. I'll walk you through the "${f.name}" flow. Answer each question and I'll narrow down the issue.`,
    );
  }, [startFlowchartOnSession]);

  useEffect(() => {
    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  // Guided-mode answers are submitted straight to the backend session — the
  // server advances the flowchart (including auto-advancing through
  // diagnosis/action/recommendation nodes) and returns exactly what changed.
  const handleChoice = async (choice) => {
    if (!diagId || !currentNode) return;
    setMessages((m) => [...m, { id: Date.now(), sender: "user", text: choice }]);
    setIsTyping(true);
    try {
      const res = await diagnosticSessionsApi.answer(diagId, choice);
      setIsTyping(false);
      setCurrentNode(res.currentNode || null);
      setMessages((m) => [...m, ...(res.newEntries || []).map(historyToMessage)]);
    } catch (err) {
      console.error(err);
      setIsTyping(false);
      toast.error("Couldn't submit your answer. Please try again.");
    }
  };

  // Side-channel Q&A while on a flowchart step — the backend never touches
  // currentNodeId here, so the technician's position in the flow can't move.
  const askGuru = async () => {
    const q = askInput.trim();
    if (!q || !diagId || askBusy) return;
    setAskInput("");
    setMessages((m) => [...m, { id: uid(), sender: "user", text: q, aside: true }]);
    setAskBusy(true);
    setIsTyping(true);
    try {
      const res = await diagnosticSessionsApi.ask(diagId, q);
      setMessages((m) => [
        ...m,
        {
          id: uid(),
          sender: "bot",
          text: res.reply,
          aside: true,
          continueDiagnostic: true,
          sources: (res.sources || []).map((s) => sourceLabel(s)),
        },
      ]);
    } catch (err) {
      console.error(err);
      toast.error("Ask Guru couldn't answer that. Please try again.");
    } finally {
      setAskBusy(false);
      setIsTyping(false);
    }
  };

  const sendFreeMessage = async (text) => {
    const t = (text || input).trim();
    if (!t || !diagId) return;
    setFreeMessages((m) => [...m, { id: uid(), sender: "user", text: t }]);
    setInput("");
    setIsTyping(true);
    try {
      const res = await diagnosticSessionsApi.chat(diagId, t, { origin: window.location.origin });
      setIsTyping(false);
      if (res.flowSuggestion) {
        setFreeMessages((m) => [...m, { id: uid(), sender: "bot", text: res.reply, flowSuggestion: res.flowSuggestion, lastQuestion: t }]);
      } else {
        setFreeMessages((m) => [
          ...m,
          {
            id: uid(),
            sender: "bot",
            text: res.reply,
            sources: (res.sources || []).map((s) => sourceLabel(s)),
            cards: res.cards || [],
            pendingAction: res.pendingAction || null,
          },
        ]);
      }
    } catch (err) {
      console.error(err);
      setIsTyping(false);
      toast.error("Diagnostic Guru failed to respond. Check backend connection.");
      setFreeMessages((m) => [...m, { id: uid(), sender: "bot", text: "I couldn't reach the AI service. Please try again." }]);
    }
  };

  // A Free Chat message matched one of the company's flowcharts — hand off
  // to the SAME session's guided flow without losing anything either side.
  const startFromSuggestion = async (msgId, flowSuggestion) => {
    if (!diagId) return;
    setFreeMessages((m) => m.map((x) => (x.id === msgId ? { ...x, choiceMade: "guided" } : x)));
    await loadFlows();
    const f = getFlow(flowSuggestion.flowchartId);
    if (f) {
      setFlow(f);
      setActiveId(f.id);
    }
    await startFlowchartOnSession(diagId, flowSuggestion.flowchartId, `Let's walk through **"${flowSuggestion.flowchartName}"** together.`);
    setMode("guided");
  };

  // Technician opted out of the suggested flowchart — re-run the same
  // message through the normal AI diagnosis path instead.
  const continueWithAI = async (msgId, lastQuestion) => {
    if (!diagId || !lastQuestion) return;
    setFreeMessages((m) => m.map((x) => (x.id === msgId ? { ...x, choiceMade: "ai" } : x)));
    setIsTyping(true);
    try {
      const res = await diagnosticSessionsApi.chat(diagId, lastQuestion, { skipFlowchartSearch: true, origin: window.location.origin });
      setIsTyping(false);
      setFreeMessages((m) => [
        ...m,
        {
          id: uid(),
          sender: "bot",
          text: res.reply,
          sources: (res.sources || []).map((s) => sourceLabel(s)),
          cards: res.cards || [],
          pendingAction: res.pendingAction || null,
        },
      ]);
    } catch (err) {
      console.error(err);
      setIsTyping(false);
      toast.error("Diagnostic Guru failed to respond. Please try again.");
    }
  };

  // Confirm a pending write/action → execute it on the backend.
  const confirmAction = async (msg, extraArgs = {}) => {
    const pa = msg.pendingAction;
    if (!pa || !diagId) return;
    setActionBusy(msg.id);
    try {
      const res = await guruApi.agentExecute({
        sessionId: diagId,
        action: pa.action,
        args: { ...pa.args, ...extraArgs },
        origin: window.location.origin,
      });
      setFreeMessages((m) =>
        m.map((x) => (x.id === msg.id ? { ...x, resolved: true } : x)).concat({
          id: uid(),
          sender: "bot",
          text: res.reply || (res.ok ? "Done." : res.error || "Couldn't complete that."),
          result: res.ok ? res.result : null,
        }),
      );
      if (res.ok) toast.success(res.say || "Action completed");
      else toast.error(res.error || "Action failed");
    } catch (err) {
      console.error(err);
      toast.error("Action failed — please try again.");
    } finally {
      setActionBusy(null);
    }
  };

  const cancelAction = (msg) => {
    setFreeMessages((m) =>
      m.map((x) => (x.id === msg.id ? { ...x, resolved: true } : x)).concat({
        id: uid(),
        sender: "bot",
        text: "Okay, cancelled. Let me know if you'd like anything else.",
      }),
    );
  };

  const openResult = (result) => {
    if (result?.url) navigate(result.url);
  };

  // "Add to Estimate" on a resolved Pricebook product — routes through the
  // normal Free Chat → Guru agent pipeline so the LLM asks for a customer if
  // needed and shows the standard confirm card. Never invents a price: the
  // item's real Pricebook id + price are passed through in the message.
  const addProductToEstimate = (product) => {
    setMode("free");
    sendFreeMessage(
      `Add 1 \u00d7 ${product.name} (Pricebook ${product.id}, $${product.unitPrice.toFixed(2)}) to an estimate for me.`,
    );
  };

  // Recommend Product node referenced a Pricebook id that no longer exists —
  // let the technician flag it to the office instead of guessing a price.
  const flagMissingProductToOffice = (productId) => {
    setMode("free");
    sendFreeMessage(
      `Flag a missing Pricebook item ("${productId}") referenced in the diagnostic flowchart \u2014 please review and add it.`,
    );
  };

  const createEstimateManually = () => navigate("/estimates");

  const onFiles = async (fileList) => {
    const arr = Array.from(fileList);
    for (const file of arr) {
      const tempId = `up-${Date.now()}-${file.name}`;
      // Optimistic
      setFiles((prev) => [
        {
          id: tempId,
          name: file.name,
          size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          date: new Date().toISOString().slice(0, 10),
          type: file.name.split(".").pop().toLowerCase(),
          status: "Indexing",
        },
        ...prev,
      ]);
      try {
        const result = await manualsApi.upload(file);
        setFiles((prev) =>
          prev.map((x) =>
            x.id === tempId
              ? {
                  id: result.id,
                  name: result.name,
                  size: `${(result.size / 1024).toFixed(0)} KB`,
                  date: (result.uploadedAt || "").slice(0, 10),
                  type: (result.ext || "").replace(".", ""),
                  status: result.status,
                  chunks: result.chunks,
                }
              : x,
          ),
        );
        toast.success(`Indexed ${result.name} (${result.chunks} chunk${result.chunks === 1 ? "" : "s"})`);
      } catch (err) {
        console.error(err);
        setFiles((prev) => prev.filter((x) => x.id !== tempId));
        toast.error(`Failed to index ${file.name}`);
      }
    }
  };

  const removeFile = async (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    try {
      await manualsApi.remove(id);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete manual");
    }
  };

  const handleImageUpload = (fileList) => {
    if (!fileList || fileList.length === 0 || !diagId) return;
    const file = fileList[0];
    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setMessages((m) => [
        ...m,
        {
          id: uid(),
          sender: "user",
          imageUrl: dataUrl,
          imageName: file.name,
          text: "",
        },
      ]);
      setAnalyzing(true);
      setIsTyping(true);
      try {
        const res = await diagnosticSessionsApi.chat(
          diagId,
          "I'm uploading an equipment photo. Please identify what you see, extract any visible readings, gauges, or labels, and tell me what's likely wrong.",
          { imageBase64: dataUrl, origin: window.location.origin },
        );
        setIsTyping(false);
        setAnalyzing(false);
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            sender: "bot",
            text: res.reply,
            sources: (res.sources || []).map((s) => sourceLabel(s)),
            cards: res.cards || [],
            pendingAction: res.pendingAction || null,
          },
        ]);
      } catch (err) {
        console.error(err);
        setIsTyping(false);
        setAnalyzing(false);
        toast.error("Vision analysis failed");
      }
    };
    reader.readAsDataURL(file);
  };

  const showChoices = mode === "guided" && currentNode && currentNode.type === "questionNode";
  const showMeasurementInput = mode === "guided" && currentNode && currentNode.type === "inputNode";
  const [measurementDraft, setMeasurementDraft] = useState("");

  usePageHeader({
    title: "Diagnostic Guru — AI Diagnostic Assistant",
    subtitle: "Trained on your service manuals & flow charts",
    actions: (
      <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg p-1">
        <button
          onClick={() => setMode("guided")}
          data-testid="guru-mode-guided"
          className={`h-7 px-3 rounded-md text-[12px] font-medium flex items-center gap-1.5 transition-colors ${
            mode === "guided" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Workflow className="h-3.5 w-3.5" /> Guided
        </button>
        <button
          onClick={() => setMode("free")}
          data-testid="guru-mode-free"
          className={`h-7 px-3 rounded-md text-[12px] font-medium flex items-center gap-1.5 transition-colors ${
            mode === "free" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <MessageSquare className="h-3.5 w-3.5" /> Free Chat
        </button>
      </div>
    ),
  });

  return (
    <>
      <div className="flex-1 p-6">
        <div className="h-[calc(100vh-160px)] grid grid-cols-12 gap-4">
          {/* Left: trained sources */}
          {diagnosticsEnabled ? (
          <div className="col-span-4 xl:col-span-3 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
            <div className="px-4 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[14px] font-semibold text-slate-900">Trained Sources</div>
                <div className="text-[11.5px] text-slate-500">{files.length} files indexed</div>
              </div>
              <div className="flex items-center gap-1.5">
                {hasModule("ai_knowledge_base") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[12px] border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => setKbOpen(true)}
                  data-testid="guru-open-kb-btn"
                  title="Open the AI Knowledge Base — upload service manuals, bulletins, spec sheets with rich metadata"
                >
                  <Book className="h-3.5 w-3.5 mr-1" /> Knowledge Base
                </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[12px] border-slate-300 text-slate-700 hover:bg-slate-50"
                  onClick={() => setHistoryOpen(true)}
                  data-testid="guru-open-history-btn"
                  title="Diagnostic History — review completed diagnostic sessions"
                >
                  <History className="h-3.5 w-3.5 mr-1" /> History
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[12px]"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-3.5 w-3.5 mr-1" /> Upload
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-slate-500"
                  title="Re-index sources"
                  data-testid="manuals-reindex-btn"
                  disabled={reindexing}
                  onClick={handleReindex}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${reindexing ? "animate-spin" : ""}`} />
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => onFiles(e.target.files)}
              />
            </div>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
              }}
              className={`mx-3 mt-3 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
                dragOver ? "border-blue-400 bg-blue-50/60" : "border-slate-200 bg-slate-50/40"
              }`}
            >
              <UploadCloud className="h-6 w-6 text-slate-400 mx-auto mb-1" />
              <div className="text-[12px] text-slate-600">Drag manuals here to train Diagnostic Guru</div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">PDF, DOCX, TXT, JPG — up to 25 MB</div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
              {files.map((f) => (
                <div
                  key={f.id}
                  className="group flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="h-8 w-8 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium text-slate-800 truncate">{f.name}</div>
                    <div className="text-[10.5px] text-slate-500 flex items-center gap-1.5">
                      <span>{f.size}</span>
                      <span>·</span>
                      <span className="flex items-center gap-0.5">
                        {f.status === "Indexed" ? (
                          <>
                            <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" />
                            <span className="text-emerald-600 font-medium">Indexed</span>
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-2.5 w-2.5 text-amber-500 animate-spin" />
                            <span className="text-amber-600 font-medium">Indexing</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(f.id)}
                    className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded hover:bg-red-50 text-red-500 flex items-center justify-center transition-all"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          ) : (
          <div className="col-span-4 xl:col-span-3 bg-white rounded-xl border border-slate-200 flex flex-col items-center justify-center p-6 text-center" data-testid="guru-diagnostics-locked">
            <Book className="h-8 w-8 text-slate-300 mb-2" />
            <div className="text-[13px] font-semibold text-slate-700">AI Diagnostic Assistant</div>
            <div className="text-[11.5px] text-slate-400 mt-1">Manual & equipment knowledge base isn't included in your plan.</div>
          </div>
          )}

          {/* Right: chat */}
          <div className="col-span-8 xl:col-span-9 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-[14px] font-semibold text-slate-900 flex items-center gap-1.5">
                    Diagnostic Guru
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-br from-fuchsia-500 to-blue-500 text-white">
                      AI
                    </span>
                  </div>
                  <div className="text-[11.5px] text-slate-500">
                    {mode === "guided" ? `Following: ${flow.name}` : `Free chat \u2014 ${files.filter(f => f.status === "Indexed").length} sources`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {mode === "guided" && flowsList.length > 0 && (
                  <select
                    value={getActiveId() || ""}
                    onChange={(e) => switchFlow(e.target.value)}
                    className="h-8 px-2 pr-7 rounded-md border border-slate-200 bg-white text-[12px] text-slate-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 cursor-pointer"
                  >
                    {flowsList.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[12px]"
                  data-testid="guru-restart-btn"
                  onClick={async () => {
                    // Fully reset transient chat state so a click always
                    // produces a visible fresh conversation.
                    setInput("");
                    setIsTyping(false);
                    setActionBusy(null);
                    if (mode === "guided") {
                      const activeFlowId = getActiveId();
                      const f = activeFlowId ? getFlow(activeFlowId) : null;
                      if (f && diagId) {
                        await startFlowchartOnSession(
                          diagId,
                          f.id,
                          `Hi, I'm **Diagnostic Guru** \u2014 your diagnostic assistant. I'll walk you through the "${f.name}" flow. Answer each question and I'll narrow down the issue.`,
                        );
                      } else {
                        setGuidedMessages([{ id: 1, sender: "bot", text: "No diagnostic flow available. Build one in the Flow Chart page first." }]);
                      }
                    } else {
                      setFreeMessages([{
                        id: uid(),
                        sender: "bot",
                        text: `Hi, I'm **Diagnostic Guru**. Describe an issue and I'll check for a matching company procedure first, or ask a diagnostic question. I can also **build an estimate, convert it to an invoice, email a document, book an appointment, create a task, or message a teammate** (you confirm before anything is saved).`,
                      }]);
                    }
                    toast.success("Chat restarted");
                  }}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" color="#334155" strokeWidth={2.5} /> Restart
                </Button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-[#FAFBFD]">
              {messages.map((m) => (
                <div key={m.id}>
                  <MessageBubble
                    message={m}
                    onStartGuided={startFromSuggestion}
                    onContinueWithAI={continueWithAI}
                    onAddToEstimate={addProductToEstimate}
                    onFlagMissing={flagMissingProductToOffice}
                    onCreateEstimateManually={createEstimateManually}
                  />
                  {(m.cards?.length > 0 || m.pendingAction || m.result) && (
                    <div className="ml-[42px] mt-1 max-w-[78%]">
                      {(m.cards || []).map((c, i) => (
                        <GuruLinkCard key={i} card={c} />
                      ))}
                      {m.pendingAction && !m.resolved && (
                        <GuruActionCard
                          pending={m.pendingAction}
                          busy={actionBusy === m.id}
                          onConfirm={(extra) => confirmAction(m, extra)}
                          onCancel={() => cancelAction(m)}
                        />
                      )}
                      {m.result && <GuruResultCard result={m.result} onOpen={openResult} />}
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="flex gap-2.5 items-start">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2.5">
                    {analyzing ? (
                      <div className="flex items-center gap-2 text-[12.5px] text-slate-600">
                        <ScanSearch className="h-3.5 w-3.5 text-violet-600 animate-pulse" />
                        <span>Analyzing image with vision AI…</span>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Image upload bar (always visible, both modes) */}
            <div className="px-5 py-2 border-t border-slate-100 bg-gradient-to-r from-violet-50/50 to-blue-50/50 flex items-center gap-2">
              <ScanSearch className="h-4 w-4 text-violet-600 shrink-0" />
              <span className="text-[11.5px] text-slate-600 font-medium flex-1">
                Upload a photo of gauges, panels, or parts — Diagnostic Guru will read it and start a diagnosis
              </span>
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={analyzing}
                className="h-8 px-2.5 rounded-md bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-[12px] font-medium text-slate-700 flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Paperclip className="h-3.5 w-3.5 text-violet-600" />
                Upload Image
              </button>
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={analyzing}
                className="h-8 px-2.5 rounded-md bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-[12px] font-medium text-slate-700 flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera className="h-3.5 w-3.5 text-violet-600" />
                Take Photo
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  handleImageUpload(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                  handleImageUpload(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>

            {/* Ask Guru — side-channel Q&A that never advances the flowchart */}
            {mode === "guided" && diagId && currentNode && (
              <div className="px-5 py-2.5 border-t border-slate-100 bg-amber-50/40 flex items-center gap-2" data-testid="guru-ask-bar">
                <HelpCircle className="h-4 w-4 text-amber-600 shrink-0" />
                <input
                  value={askInput}
                  onChange={(e) => setAskInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      askGuru();
                    }
                  }}
                  placeholder="Ask Guru about this step — won't change your progress"
                  data-testid="guru-ask-input"
                  disabled={askBusy}
                  className="flex-1 h-8 px-2.5 rounded-md border border-amber-200 bg-white text-[12.5px] focus:outline-none focus:ring-2 focus:ring-amber-400/30 disabled:opacity-60"
                />
                <button
                  onClick={askGuru}
                  disabled={!askInput.trim() || askBusy}
                  data-testid="guru-ask-send"
                  className="h-8 px-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-semibold disabled:opacity-50 transition-colors"
                >
                  Ask
                </button>
              </div>
            )}

            {/* Measurement / free-text input for inputNode steps in guided mode */}
            {showMeasurementInput && (
              <div className="px-5 py-3 border-t border-slate-100 bg-white" data-testid="guru-measurement-input">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                  {currentNode.data.placeholder || "Enter a value"}
                </div>
                <div className="flex gap-2">
                  <input
                    value={measurementDraft}
                    onChange={(e) => setMeasurementDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && measurementDraft.trim()) {
                        e.preventDefault();
                        handleChoice(measurementDraft.trim());
                        setMeasurementDraft("");
                      }
                    }}
                    placeholder={currentNode.data.placeholder || "Type your answer\u2026"}
                    data-testid="guru-measurement-value"
                    className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                  <button
                    onClick={() => {
                      if (measurementDraft.trim()) {
                        handleChoice(measurementDraft.trim());
                        setMeasurementDraft("");
                      }
                    }}
                    disabled={!measurementDraft.trim()}
                    data-testid="guru-measurement-submit"
                    className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold disabled:opacity-50 transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </div>
            )}

            {/* Choices for guided mode */}
            {showChoices && (
              <div className="px-5 py-3 border-t border-slate-100 bg-white" data-testid="guru-guided-choices">
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Your answer</div>
                <div className="flex flex-wrap gap-2">
                  {(currentNode.data.choices || ["Yes", "No"]).map((c) => (
                    <button
                      key={c}
                      onClick={() => handleChoice(c)}
                      className="px-3.5 py-2 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-[13px] font-medium text-slate-700 transition-colors flex items-center gap-1.5"
                    >
                      {c}
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Free chat input */}
            {mode === "free" && (
              <div className="px-5 py-3 border-t border-slate-100 bg-white">
                {messages.length <= 1 && (
                  <div className="mb-2.5 flex flex-wrap gap-1.5">
                    {suggestedPrompts.map((p) => (
                      <button
                        key={p}
                        onClick={() => sendFreeMessage(p)}
                        className="text-[11.5px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 transition-colors flex items-center gap-1"
                      >
                        <Sparkles className="h-3 w-3" />
                        {p}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendFreeMessage();
                      }
                    }}
                    rows={1}
                    placeholder="Ask Guru about a diagnostic, manual, or repair procedure..."
                    data-testid="guru-free-input"
                    className="flex-1 resize-none px-3 py-2.5 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-h-32"
                  />
                  <Button
                    onClick={() => sendFreeMessage()}
                    data-testid="guru-free-send"
                    className="h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4"
                    disabled={!input.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <KnowledgeBaseModal open={kbOpen} onClose={() => setKbOpen(false)} />
      <DiagnosticHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

function MessageBubble({ message, onStartGuided, onContinueWithAI, onAddToEstimate, onFlagMissing, onCreateEstimateManually }) {
  const isUser = message.sender === "user";
  if (isUser) {
    return (
      <div className="flex gap-2.5 items-start justify-end">
        <div className="max-w-[70%]">
          {message.imageUrl && (
            <div className="mb-1.5 rounded-2xl rounded-tr-sm overflow-hidden border border-slate-200 bg-white">
              <img src={message.imageUrl} alt={message.imageName || "uploaded"} className="block max-h-72 w-auto" />
              {message.imageName && (
                <div className="px-2.5 py-1 text-[10.5px] text-slate-500 bg-slate-50 border-t border-slate-100 truncate">
                  {message.imageName}
                </div>
              )}
            </div>
          )}
          {message.text && (
            <div className="bg-[#2563EB] text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5">
              <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap">{message.text}</p>
            </div>
          )}
        </div>
        <div className="h-8 w-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
          <User className="h-4 w-4 text-slate-600" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5 items-start">
      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center shrink-0">
        <Bot className="h-4 w-4 text-white" />
      </div>
      <div className="max-w-[78%]">
        <div
          className={`rounded-2xl rounded-tl-sm px-3.5 py-2.5 border ${
            message.aside
              ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
              : message.diagnosis
                ? "bg-gradient-to-br from-violet-50 to-blue-50 border-violet-200"
                : message.recommendation
                  ? "bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200"
                  : message.visionReadings
                    ? "bg-gradient-to-br from-fuchsia-50 to-violet-50 border-violet-200"
                    : "bg-white border-slate-200"
          }`}
          data-testid={message.aside ? "guru-ask-reply-bubble" : undefined}
        >
          {message.aside && (
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-1 flex items-center gap-1">
              <HelpCircle className="h-3 w-3" /> Ask Guru (side question)
            </div>
          )}
          <p className="text-[13.5px] text-slate-800 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: sanitizeHtml(formatMarkdown(message.text)) }} />

          {message.continueDiagnostic && (
            <button
              onClick={() => document.querySelector('[data-testid="guru-guided-choices"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
              data-testid="guru-continue-diagnostic-btn"
              className="mt-2.5 pt-2 border-t border-amber-200/70 text-[12px] font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1 transition-colors"
            >
              <ChevronDown className="h-3.5 w-3.5" /> Continue Diagnostic {"\u2014"} you're still on the same step
            </button>
          )}

          {message.flowSuggestion && (
            <div className="mt-2.5 pt-2.5 border-t border-slate-200/70 flex flex-wrap gap-2" data-testid="guru-flow-suggestion">
              {message.choiceMade ? (
                <div className="text-[12px] text-slate-500 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  {message.choiceMade === "guided" ? "Starting the guided procedure\u2026" : "Continuing with AI\u2026"}
                </div>
              ) : (
                <>
                  <button
                    onClick={() => onStartGuided?.(message.id, message.flowSuggestion)}
                    data-testid="guru-start-guided-procedure-btn"
                    className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <Workflow className="h-3.5 w-3.5" /> Start Guided Procedure
                  </button>
                  <button
                    onClick={() => onContinueWithAI?.(message.id, message.lastQuestion)}
                    data-testid="guru-continue-with-ai-btn"
                    className="h-8 px-3 rounded-md bg-white border border-slate-200 hover:border-blue-300 text-slate-700 text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <MessageSquare className="h-3.5 w-3.5" /> Continue With AI
                  </button>
                </>
              )}
            </div>
          )}

          {message.visionReadings && (
            <div className="mt-2.5 space-y-1">
              {message.visionReadings.map((r, i) => {
                const flagColor =
                  r.flag === "alert"
                    ? "text-red-600 bg-red-50 border-red-200"
                    : r.flag === "low"
                      ? "text-amber-700 bg-amber-50 border-amber-200"
                      : "text-emerald-700 bg-emerald-50 border-emerald-200";
                return (
                  <div key={i} className="flex items-center gap-2 bg-white rounded-md border border-violet-100 px-2 py-1.5">
                    <ScanSearch className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                    <div className="text-[12px] text-slate-600 flex-1">{r.label}</div>
                    <div className={`text-[12px] font-semibold px-1.5 py-0.5 rounded border ${flagColor}`}>
                      {r.flag === "alert" && <AlertTriangle className="inline h-3 w-3 mr-0.5 -mt-0.5" />}
                      {r.value}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {message.detail && (
            <div className="mt-2 pt-2 border-t border-violet-200/60 text-[12.5px] text-slate-600">
              <span className="font-semibold text-violet-700">Recommended action: </span>
              {message.detail}
            </div>
          )}
          {message.products && message.products.length > 0 && (
            <div className="mt-2 pt-2 border-t border-sky-200/60 space-y-1.5">
              {message.products.map((p) =>
                p.found ? (
                  <div key={p.id} data-testid={`guru-product-${p.id}`} className="flex items-center gap-2 bg-white rounded-md border border-sky-100 px-2 py-1.5">
                    <div className="h-7 w-7 rounded-md bg-sky-50 flex items-center justify-center shrink-0">
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-sky-600" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-slate-800 truncate">{p.name}</div>
                      <div className="text-[10.5px] text-slate-500 flex items-center gap-1">
                        {p.sku && <span className="font-mono">{p.sku}</span>}
                        {p.sku && <span>·</span>}
                        <span>{p.kind === "material" ? "Material" : "Service"}</span>
                        {p.kind === "material" && (
                          <span className={p.available ? "text-emerald-600" : "text-rose-600 font-semibold"}>
                            · {p.available ? `${p.stockOnHand} in stock` : "Out of stock"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div className="text-[13px] font-bold text-emerald-700">${p.unitPrice.toFixed(2)}</div>
                      <button
                        onClick={() => onAddToEstimate?.(p)}
                        data-testid={`guru-add-to-estimate-${p.id}`}
                        className="h-6 px-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-[10.5px] font-semibold transition-colors"
                      >
                        Add to Estimate
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={p.id} data-testid={`guru-product-missing-${p.id}`} className="flex flex-col gap-1.5 bg-amber-50 rounded-md border border-amber-200 px-2.5 py-2">
                    <div className="text-[12px] text-amber-800 font-medium flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> No matching Pricebook item found ({p.id})
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => onFlagMissing?.(p.id)}
                        data-testid={`guru-send-to-office-${p.id}`}
                        className="h-6 px-2 rounded-md bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 text-[10.5px] font-semibold transition-colors"
                      >
                        Send to Office
                      </button>
                      <button
                        onClick={() => onCreateEstimateManually?.()}
                        data-testid="guru-create-estimate-manually"
                        className="h-6 px-2 rounded-md bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 text-[10.5px] font-semibold transition-colors"
                      >
                        Create Estimate Manually
                      </button>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
          {message.sources && message.sources.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Sources:</span>
              {message.sources.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                  <FileText className="h-2.5 w-2.5" />
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatMarkdown(t) {
  if (!t) return "";
  // simple bold **x**
  return t
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}
